import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave, FiCreditCard, FiAlertCircle } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function CryptoWalletConfigModal({ isOpen, onClose, website, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [manualWallets, setManualWallets] = useState({
    BTC: { address: '', network: 'mainnet' },
    ETH: { address: '', network: 'mainnet' },
    USDT: { address: '', network: 'ethereum' },
    'USDT (TRC20)': { address: '' },
    'USDT (ERC20)': { address: '' },
    BNB: { address: '', network: 'mainnet' },
    SOL: { address: '', network: 'mainnet' },
    DOGE: { address: '', network: 'mainnet' },
    XRP: { address: '', tag: '' },
    LTC: { address: '', network: 'mainnet' },
    ADA: { address: '', network: 'mainnet' },
    DOT: { address: '', network: 'mainnet' }
  })

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setManualWallets({
        BTC: { address: '', network: 'mainnet' },
        ETH: { address: '', network: 'mainnet' },
        USDT: { address: '', network: 'ethereum' },
        'USDT (TRC20)': { address: '' },
        'USDT (ERC20)': { address: '' },
        BNB: { address: '', network: 'mainnet' },
        SOL: { address: '', network: 'mainnet' },
        DOGE: { address: '', network: 'mainnet' },
        XRP: { address: '', tag: '' },
        LTC: { address: '', network: 'mainnet' },
        ADA: { address: '', network: 'mainnet' },
        DOT: { address: '', network: 'mainnet' }
      })
    }
  }, [isOpen])

  // Fetch wallet configuration
  useEffect(() => {
    if (isOpen && website) {
      fetchWalletConfig()
    }
  }, [isOpen, website])

  const fetchWalletConfig = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('globalAdminToken') || localStorage.getItem('userAdminToken') || localStorage.getItem('token')
      console.log('[CryptoWalletConfigModal] Fetching wallet config for website:', website._id)
      const response = await axios.get(`/crypto/config/wallet/${website._id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      console.log('[CryptoWalletConfigModal] Wallet config response:', response.data)
      
      if (response.data.manualWallets) {
        setManualWallets(prev => ({
          ...prev,
          ...response.data.manualWallets
        }))
      }
    } catch (error) {
      console.error('[CryptoWalletConfigModal] Error fetching wallet config:', error)
      console.error('[CryptoWalletConfigModal] Status:', error.response?.status)
      console.error('[CryptoWalletConfigModal] Data:', error.response?.data)
      const message = error.response?.data?.message || 'Failed to load wallet configuration'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleWalletAddressChange = (crypto, field, value) => {
    setManualWallets(prev => ({
      ...prev,
      [crypto]: {
        ...prev[crypto],
        [field]: value
      }
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate that at least one manual wallet is configured
    let hasAtLeastOne = false
    for (const [crypto, wallet] of Object.entries(manualWallets)) {
      if (wallet.address?.trim()) {
        hasAtLeastOne = true
        break
      }
    }
    if (!hasAtLeastOne) {
      toast.error('Please configure at least one manual wallet address')
      return
    }

    setSaving(true)
    try {
      const payload = {
        manualWallets: Object.fromEntries(
          Object.entries(manualWallets).filter(([, wallet]) => wallet.address?.trim())
        )
      }

      const token = localStorage.getItem('globalAdminToken') || localStorage.getItem('userAdminToken') || localStorage.getItem('token')
      await axios.put(`/crypto/config/wallet/${website._id}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      
      toast.success('Wallet configuration updated successfully')
      if (onSuccess) onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating wallet config:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update wallet configuration'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FiCreditCard className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Crypto Wallet Configuration</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 px-6">
          <button
            className="px-4 py-3 font-medium border-b-2 border-blue-600 text-blue-600"
          >
            Manual Wallets Configuration
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FiLoader className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Configure Wallet Addresses</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Add wallet addresses for cryptocurrencies you accept. Leave blank to disable.
                </p>
              </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* BTC */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Bitcoin (BTC)</label>
                    <input
                      type="text"
                      placeholder="Bitcoin address (e.g., 1A1z...)"
                      value={manualWallets.BTC.address}
                      onChange={(e) => handleWalletAddressChange('BTC', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                    <select
                      value={manualWallets.BTC.network}
                      onChange={(e) => handleWalletAddressChange('BTC', 'network', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="mainnet">Mainnet</option>
                      <option value="testnet">Testnet</option>
                    </select>
                  </div>

                  {/* ETH */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Ethereum (ETH)</label>
                    <input
                      type="text"
                      placeholder="Ethereum address (0x...)"
                      value={manualWallets.ETH.address}
                      onChange={(e) => handleWalletAddressChange('ETH', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* USDT (TRC20) */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">USDT (TRC20)</label>
                    <input
                      type="text"
                      placeholder="TRC20 USDT address"
                      value={manualWallets['USDT (TRC20)']?.address || ''}
                      onChange={(e) => handleWalletAddressChange('USDT (TRC20)', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* USDT (ERC20) */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">USDT (ERC20)</label>
                    <input
                      type="text"
                      placeholder="ERC20 USDT address"
                      value={manualWallets['USDT (ERC20)']?.address || ''}
                      onChange={(e) => handleWalletAddressChange('USDT (ERC20)', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* USDT Generic Fallback */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">USDT (Fallback)</label>
                    <input
                      type="text"
                      placeholder="Generic USDT address"
                      value={manualWallets.USDT?.address || ''}
                      onChange={(e) => handleWalletAddressChange('USDT', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                    <select
                      value={manualWallets.USDT?.network || 'ethereum'}
                      onChange={(e) => handleWalletAddressChange('USDT', 'network', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="ethereum">Ethereum (ERC20)</option>
                      <option value="tron">TRON (TRC20)</option>
                      <option value="binance">Binance Smart Chain (BEP20)</option>
                    </select>
                  </div>

                  {/* BNB */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">BNB</label>
                    <input
                      type="text"
                      placeholder="BNB address"
                      value={manualWallets.BNB.address}
                      onChange={(e) => handleWalletAddressChange('BNB', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* SOL */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Solana (SOL)</label>
                    <input
                      type="text"
                      placeholder="Solana address"
                      value={manualWallets.SOL.address}
                      onChange={(e) => handleWalletAddressChange('SOL', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* DOGE */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Dogecoin (DOGE)</label>
                    <input
                      type="text"
                      placeholder="Dogecoin address (D...)"
                      value={manualWallets.DOGE.address}
                      onChange={(e) => handleWalletAddressChange('DOGE', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* XRP */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">XRP (with Tag)</label>
                    <input
                      type="text"
                      placeholder="XRP address"
                      value={manualWallets.XRP.address}
                      onChange={(e) => handleWalletAddressChange('XRP', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                    <input
                      type="text"
                      placeholder="Destination tag"
                      value={manualWallets.XRP.tag}
                      onChange={(e) => handleWalletAddressChange('XRP', 'tag', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* LTC */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Litecoin (LTC)</label>
                    <input
                      type="text"
                      placeholder="Litecoin address (L...)"
                      value={manualWallets.LTC.address}
                      onChange={(e) => handleWalletAddressChange('LTC', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* ADA */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Cardano (ADA)</label>
                    <input
                      type="text"
                      placeholder="Cardano address"
                      value={manualWallets.ADA.address}
                      onChange={(e) => handleWalletAddressChange('ADA', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* DOT */}
                  <div className="space-y-3">
                    <label className="block font-semibold text-gray-700">Polkadot (DOT)</label>
                    <input
                      type="text"
                      placeholder="Polkadot address (1...)"
                      value={manualWallets.DOT.address}
                      onChange={(e) => handleWalletAddressChange('DOT', 'address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                  <h4 className="font-semibold text-amber-900 mb-2">⚠️ Security Note</h4>
                  <p className="text-sm text-amber-800">
                    Only add addresses you control and can receive payments to. Keep your wallet addresses secure.
                  </p>
                </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
